/**
 * Air in the room.
 *
 * The table currently reads as a vacuum: every surface is lit, nothing is
 * *between* the eye and those surfaces. This module puts a handful of slow dust
 * motes in the space above the felt, visible only where they drift through the
 * key light and invisible everywhere else. That visibility gradient is the
 * whole effect — it is what ties the air to the same `LIGHT` the cards, their
 * shadows and the felt are already shaded by, and it is why the dust reads as
 * lit particles rather than as sprinkled white dots.
 *
 * ## How it is built
 *
 * One `Mesh`, one custom GLSL program, one static vertex buffer, **one draw
 * call**, and exactly two scalar uniforms. Every mote is a quad whose position,
 * brightness, colour and twinkle are computed *in the vertex shader* from a
 * per-mote seed and a single `uTime`. There is no CPU simulation at all: the
 * per-frame cost of this module is one float write, no matter how many motes
 * there are. Nothing here allocates after construction.
 *
 * ## The visibility model
 *
 * A mote is a point at `(x, y, h)` — `h` being its virtual height above the
 * felt — lit by the same two lights the table shader uses:
 *
 * - the warm **key** at `LIGHT`, above and to the left, raking across the table;
 * - the cool **fill** at `LIGHT.fill`, low on the right.
 *
 * Each contributes `1 / (1 + d² / ref²)`, the identical falloff `table.ts`
 * applies to the felt, so a mote brightens for exactly the same reason a patch
 * of felt does. Raw, that falloff only spans about 2:1 across the table, which
 * would give a flat wash of dust everywhere. So each term is normalised against
 * its own maximum over the table and then contrast-stretched by
 * {@link AtmosphereOptions.contrast}: 25:1 or so between the pool under the key
 * light and the far corner, which is what makes the motes appear as they enter
 * the light and dissolve as they leave it. The stretch is the one piece of
 * art direction here — the shape of the gradient is the light's, not ours.
 *
 * Colour comes from the two light colours, so a mote in the key is warm and a
 * mote out on the right picks up the cool bounce. Blending is additive: dust
 * scatters light toward the eye, it never occludes anything.
 *
 * ## Drift
 *
 * Slow linear drift plus a two-axis Lissajous wander, both evaluated in the
 * vertex shader, wrapped through a domain one {@link DUST.margin} larger than
 * the design rect on every side. Alpha fades to zero across that margin, so the
 * wrap always happens on an invisible mote and never pops.
 *
 * Every frequency in the shader is an integer harmonic of one loop period, and
 * every drift velocity crosses a whole number of domain spans in that period.
 * The entire field is therefore exactly periodic, `uTime` is wrapped at the
 * period, and the float that drives everything never grows past a few hundred —
 * no precision decay, no discontinuity, in a session of any length.
 *
 * ## Cost and control
 *
 * 64 motes is 256 vertices and 384 indices, uploaded once. Fill is a few
 * thousand fragments per frame. `intensity: 0` hides the mesh outright, so the
 * effect costs literally nothing when it is off, and `prefers-reduced-motion`
 * freezes the drift rather than removing the air.
 *
 * ## Wiring
 *
 * ```ts
 * const atmosphere = createAtmosphere();
 * pixi.layers.shadows.addChild(atmosphere.view); // above the felt, under the cards
 * pixi.onUpdate((dt) => atmosphere.update(dt));
 * ```
 *
 * It must sit below the `cards` layer: dust between the eye and the cards would
 * fog the faces the player is reading.
 */

import {
  Buffer,
  BufferUsage,
  Geometry,
  Mesh,
  Rectangle,
  Shader,
  UniformGroup,
} from 'pixi.js';

import { CARD, DESIGN, LIGHT } from '@/pixi/layout';

/* ------------------------------------------------------------------------ *
 * Tuning. Sizes and heights are in design units; at the 1920x1080 authoring
 * resolution one design unit is one screen pixel, which is the scale the
 * "sub-pixel to 2px" brief for a dust mote is written in.
 * ------------------------------------------------------------------------ */

export const DUST = {
  /** Motes. Few enough that the player cannot count them. */
  count: 64,

  /**
   * Gaussian sigma of a mote, in design units. The smallest is a sub-pixel
   * speck, the largest a soft two-pixel blob.
   */
  sigmaMin: 0.6,
  sigmaMax: 1.9,
  /** Quad half-size in sigmas. Three sigmas puts the cut-off at 1% of peak. */
  quad: 3.0,
  /**
   * Floor on a mote's on-screen sigma, in *screen* pixels. Below about half a
   * pixel a slow-moving speck scintillates as it crosses the sample grid, which
   * is exactly the artefact that makes cheap dust look cheap. A mote grown to
   * this floor has its alpha scaled by the area ratio, so growing it preserves
   * the total light it contributes rather than brightening it.
   */
  minScreenSigma: 0.5,

  /**
   * Peak additive alpha, at the smallest sigma. Large motes are dimmer — near
   * dust is more out of focus and spreads the same light over more area — which
   * is the cheapest possible depth cue and costs one lerp.
   */
  alphaPeak: 0.13,
  /** Fraction of `alphaPeak` the largest mote gets. */
  alphaFalloff: 0.42,

  /** Virtual height above the felt. Cards live at 0; the key light at LIGHT.z. */
  heightMin: 20,
  heightMax: 150,

  /**
   * Contrast stretch on the normalised light falloff. Higher pulls the visible
   * dust into a tighter pool under the key. Below ~3 the dust reads as an even
   * haze, which is the failure mode this effect is trying to avoid.
   */
  contrast: 5.5,
  /**
   * Reference distance for the inverse-square-ish falloff. Matches the constant
   * the felt is lit with in `table.ts`, so both fall off together.
   */
  reference: 1500,
  /**
   * How much of the cool fill a mote is allowed to scatter, on top of its own
   * `LIGHT.fill.intensity`. The fill sits low and *inside* the table, so at full
   * weight it lights a second pool of blue dust on the right that competes with
   * the key — decoration, and cool decoration at that. Held down to a whisper it
   * does the one job it should: quietly cooling the motes on that side.
   */
  fillWeight: 0.35,

  /**
   * Seconds for the whole field to return exactly to its starting state. Also
   * the wrap point for `uTime`. Longer than a 180s round, so a player never
   * sees the same dust twice.
   */
  loop: 600,

  /**
   * Drift, in whole domain-crossings per loop. A mote with `1` crosses the
   * table once in `loop` seconds (~3.5 units/second) — a slow draught, not a
   * breeze. Weighted to the right and slightly upward, so the air has a
   * prevailing direction the way a warm room does.
   */
  driftX: [1, 1, 2, 2, 3, -1, -2],
  driftY: [-1, -1, 0, 0, 1],

  /** Wander amplitude in design units, and its rate in harmonics of the loop. */
  wanderMin: 8,
  wanderMax: 22,
  wanderHarmonicMin: 6,
  wanderHarmonicMax: 26,

  /**
   * Twinkle — a tumbling mote catching the light at different angles. Slow and
   * shallow: a visible blink turns dust into fireflies.
   */
  twinkleDepth: 0.25,
  twinkleHarmonicMin: 90,
  twinkleHarmonicMax: 260,

  /** How far past the design rect the wrap domain reaches, and the fade width. */
  margin: CARD.width * 0.45,

  /** Fixed seed, so the dust field is the same every run and can be judged. */
  seed: 0x2115d057,
} as const;

/** Longest simulation step. A backgrounded tab must not lurch the field. */
const MAX_STEP = 1 / 15;

/* ------------------------------------------------------------------------ *
 * Small maths helpers.
 * ------------------------------------------------------------------------ */

const TAU = Math.PI * 2;

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Format a JS number as a GLSL float literal, always with a decimal point and
 * never rounded away — the falloff constants are around 4e-7, so a naive
 * `toFixed` would silently delete the whole attenuation model. Same helper as
 * the one `table.ts` uses on the same constants, for the same reason.
 */
function num(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '0.0';

  const magnitude = Math.abs(v);
  if (magnitude >= 1e-4 && magnitude < 1e7) {
    const fixed = v.toFixed(9).replace(/0+$/, '');
    return fixed.endsWith('.') ? `${fixed}0` : fixed;
  }

  const [mantissa, exponent] = v.toExponential(9).split('e');
  const trimmed = mantissa.replace(/0+$/, '');
  const safe = trimmed.endsWith('.') ? `${trimmed}0` : trimmed;
  return `${safe}e${exponent}`;
}

function vec2(x: number, y: number): string {
  return `vec2(${num(x)}, ${num(y)})`;
}

function vec3(x: number, y: number, z: number): string {
  return `vec3(${num(x)}, ${num(y)}, ${num(z)})`;
}

/** A packed 0xRRGGBB as a GLSL vec3 in 0..1. */
function rgb3(hex: number): string {
  return vec3(((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255);
}

/** Mulberry32. Deterministic, so the field is reproducible from `seed`. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The falloff every light in this project uses, evaluated in 3D. */
function attenuation(
  light: { x: number; y: number; z: number },
  point: { x: number; y: number; z: number },
  reference: number,
): number {
  const dx = light.x - point.x;
  const dy = light.y - point.y;
  const dz = light.z - point.z;
  return 1 / (1 + (dx * dx + dy * dy + dz * dz) / (reference * reference));
}

/**
 * The brightest a mote can ever be under `light`: the closest it can get to it
 * without leaving the visible table or its height band. Normalising by this is
 * what lets the contrast stretch be a plain `pow` with a readable exponent.
 */
function peakAttenuation(
  light: { x: number; y: number; z: number },
  reference: number,
  heightMin: number,
  heightMax: number,
): number {
  return attenuation(
    light,
    {
      x: clamp(light.x, 0, DESIGN.width),
      y: clamp(light.y, 0, DESIGN.height),
      z: clamp(light.z, heightMin, heightMax),
    },
    reference,
  );
}

/* ------------------------------------------------------------------------ *
 * GLSL.
 *
 * No `#version` directive, exactly like `table.ts`: Pixi's preprocessor then
 * compiles the source as ESSL 1.00 with `in`/`out`/`finalColor` defined away,
 * so the same program runs on a WebGL1 context as well as the WebGL2 one the
 * app asks for. Everything below is inside that dialect — no dynamic loops, no
 * integer maths, no texture fetch of any kind.
 * ------------------------------------------------------------------------ */

interface Baked {
  contrast: number;
  heightMin: number;
  heightMax: number;
}

function buildVertex(baked: Baked): string {
  const lo = -DUST.margin;
  const span = { x: DESIGN.width + DUST.margin * 2, y: DESIGN.height + DUST.margin * 2 };
  const keyPeak = peakAttenuation(LIGHT, DUST.reference, baked.heightMin, baked.heightMax);
  const fillPeak = peakAttenuation(LIGHT.fill, DUST.reference, baked.heightMin, baked.heightMax);
  const invRef2 = 1 / (DUST.reference * DUST.reference);

  return /* glsl */ `
in vec2 aPosition;
in vec4 aMote;
in vec4 aWander;
in vec4 aShape;

out vec2 vLocal;
out vec4 vTint;
out vec4 vColor;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uWorldColorAlpha;
uniform vec4 uColor;
uniform vec2 uResolution;

uniform float uTime;
uniform float uIntensity;

// --- the wrap domain, one margin outside the design rect --------------------
const vec2 DOMAIN_LO = ${vec2(lo, lo)};
const vec2 DOMAIN_SPAN = ${vec2(span.x, span.y)};
const vec2 DOMAIN_HI = ${vec2(lo + span.x, lo + span.y)};
const float FADE = ${num(DUST.margin)};

// --- the same two lights the felt and the cards are shaded by, with the fill
// --- held down to DUST.fillWeight so the key stays the reason dust is visible
const vec3 KEY_POS = ${vec3(LIGHT.x, LIGHT.y, LIGHT.z)};
const vec3 KEY_COL = ${rgb3(LIGHT.colour)};
const float KEY_INT = ${num(LIGHT.intensity)};
const float KEY_NORM = ${num(1 / keyPeak)};
const vec3 FILL_POS = ${vec3(LIGHT.fill.x, LIGHT.fill.y, LIGHT.fill.z)};
const vec3 FILL_COL = ${rgb3(LIGHT.fill.colour)};
const float FILL_INT = ${num(LIGHT.fill.intensity * DUST.fillWeight)};
const float FILL_NORM = ${num(1 / fillPeak)};
const float INV_REF2 = ${num(invRef2)};
const float CONTRAST = ${num(baked.contrast)};

const float QUAD = ${num(DUST.quad)};
const float MIN_SCREEN_SIGMA = ${num(DUST.minScreenSigma)};
const float TWINKLE = ${num(DUST.twinkleDepth)};

void main(void) {
    float t = uTime;

    // Linear drift plus a two-axis wander, then wrapped. Every frequency is an
    // integer harmonic of the loop and every velocity crosses a whole number of
    // spans in it, so the field is exactly periodic and uTime can be wrapped.
    vec2 drift = aMote.xy + aMote.zw * t + aWander.x * vec2(
        sin(aWander.y * t + aWander.w),
        sin(aWander.z * t + aWander.w * 2.3 + 1.7)
    );
    vec2 p = mod(drift - DOMAIN_LO, DOMAIN_SPAN) + DOMAIN_LO;

    // The mote as a point in the same space the light lives in.
    vec3 world = vec3(p, aShape.y);

    vec3 toKey = KEY_POS - world;
    float key = 1.0 / (1.0 + dot(toKey, toKey) * INV_REF2);
    key = pow(clamp(key * KEY_NORM, 0.0, 1.0), CONTRAST);

    vec3 toFill = FILL_POS - world;
    float fill = 1.0 / (1.0 + dot(toFill, toFill) * INV_REF2);
    fill = pow(clamp(fill * FILL_NORM, 0.0, 1.0), CONTRAST);

    vec3 lit = KEY_COL * (KEY_INT * key) + FILL_COL * (FILL_INT * fill);
    // Peak channel: the hue of whichever light is winning, and how much of it.
    float level = max(lit.r, max(lit.g, lit.b));
    float amount = min(level, 1.0);

    // Fade out across the margin, so a mote is always invisible where it wraps.
    vec2 edge = min(p - DOMAIN_LO, DOMAIN_HI - p);
    float fade = smoothstep(0.0, FADE, min(edge.x, edge.y));

    float twinkle = 1.0 - TWINKLE * 0.5 * (1.0 + sin(aShape.w * t + aWander.w * 3.1));

    // Screen pixels per design unit, so a mote can be kept above the size at
    // which it would scintillate instead of drift.
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    vec3 originClip = mvp * vec3(0.0, 0.0, 1.0);
    vec3 stepClip = mvp * vec3(1.0, 0.0, 1.0);
    vec2 stepPx = vec2(
        (stepClip.x - originClip.x) * uResolution.x,
        (stepClip.y - originClip.y) * uResolution.y
    ) * 0.5;
    float pxScale = max(length(stepPx), 0.0001);

    float sigma = aShape.x;
    float grown = max(sigma, MIN_SCREEN_SIGMA / pxScale);
    // Spreading the same light over a larger disc must not brighten it.
    float energy = (sigma * sigma) / (grown * grown);

    vLocal = aPosition;
    vTint = vec4(lit / max(level, 1e-4), aShape.z * amount * fade * twinkle * energy * uIntensity);
    vColor = uColor * uWorldColorAlpha;

    gl_Position = vec4((mvp * vec3(p + aPosition * (grown * QUAD), 1.0)).xy, 0.0, 1.0);
}
`;
}

/**
 * A gaussian speck. The constant subtraction takes the edge of the quad to
 * exactly zero, so no mote ever shows the seam of its own geometry.
 */
const FRAGMENT = /* glsl */ `precision highp float;

in vec2 vLocal;
in vec4 vTint;
in vec4 vColor;

out vec4 finalColor;

const float TAIL = 0.011109;

void main(void) {
    float g = max(exp(-4.5 * dot(vLocal, vLocal)) - TAIL, 0.0) * ${num(1 / (1 - 0.011109))};
    float a = vTint.a * g;

    // Premultiplied, on an additive pass: dust adds light, it never occludes.
    finalColor = vec4(vTint.rgb * a, a) * vColor;
}
`;

/* ------------------------------------------------------------------------ *
 * Geometry. One interleaved, static buffer:
 *
 *   aPosition  vec2   quad corner, -1..1
 *   aMote      vec4   origin.xy, velocity.xy      (design units, units/second)
 *   aWander    vec4   amplitude, rateX, rateY, phase
 *   aShape     vec4   sigma, height, alpha, twinkle rate
 * ------------------------------------------------------------------------ */

const FLOATS_PER_VERTEX = 14;
const STRIDE = FLOATS_PER_VERTEX * 4;
const CORNERS = [-1, -1, 1, -1, 1, 1, -1, 1];

interface FieldSpec {
  count: number;
  loop: number;
  sigmaMin: number;
  sigmaMax: number;
  alphaPeak: number;
  heightMin: number;
  heightMax: number;
  seed: number;
}

function pick(random: () => number, from: readonly number[]): number {
  return from[Math.min(from.length - 1, Math.floor(random() * from.length))];
}

/** Uniform integer in `[min, max]`, so a harmonic stays a harmonic. */
function harmonic(random: () => number, min: number, max: number): number {
  return min + Math.floor(random() * (max - min + 1));
}

function buildGeometry(spec: FieldSpec): Geometry {
  const random = rng(spec.seed);

  const spanX = DESIGN.width + DUST.margin * 2;
  const spanY = DESIGN.height + DUST.margin * 2;
  const lo = -DUST.margin;
  const rate = TAU / spec.loop;

  const vertices = new Float32Array(spec.count * 4 * FLOATS_PER_VERTEX);
  const indices = new Uint32Array(spec.count * 6);

  for (let i = 0; i < spec.count; i++) {
    const originX = lo + random() * spanX;
    const originY = lo + random() * spanY;

    // Whole domain-crossings per loop keeps the field exactly periodic.
    const velocityX = (pick(random, DUST.driftX) * spanX) / spec.loop;
    const velocityY = (pick(random, DUST.driftY) * spanY) / spec.loop;

    const wander = DUST.wanderMin + random() * (DUST.wanderMax - DUST.wanderMin);
    const rateX = harmonic(random, DUST.wanderHarmonicMin, DUST.wanderHarmonicMax) * rate;
    const rateY = harmonic(random, DUST.wanderHarmonicMin, DUST.wanderHarmonicMax) * rate;
    const phase = random() * TAU;

    // Big motes sit further from focus, so they are larger and dimmer together.
    const size = random();
    const sigma = spec.sigmaMin + size * (spec.sigmaMax - spec.sigmaMin);
    const alpha = spec.alphaPeak * (1 - (1 - DUST.alphaFalloff) * size);
    const height = spec.heightMin + random() * (spec.heightMax - spec.heightMin);
    const twinkle = harmonic(random, DUST.twinkleHarmonicMin, DUST.twinkleHarmonicMax) * rate;

    const base = i * 4;
    for (let corner = 0; corner < 4; corner++) {
      const v = (base + corner) * FLOATS_PER_VERTEX;
      vertices[v] = CORNERS[corner * 2];
      vertices[v + 1] = CORNERS[corner * 2 + 1];
      vertices[v + 2] = originX;
      vertices[v + 3] = originY;
      vertices[v + 4] = velocityX;
      vertices[v + 5] = velocityY;
      vertices[v + 6] = wander;
      vertices[v + 7] = rateX;
      vertices[v + 8] = rateY;
      vertices[v + 9] = phase;
      vertices[v + 10] = sigma;
      vertices[v + 11] = height;
      vertices[v + 12] = alpha;
      vertices[v + 13] = twinkle;
    }

    const index = i * 6;
    indices[index] = base;
    indices[index + 1] = base + 1;
    indices[index + 2] = base + 2;
    indices[index + 3] = base;
    indices[index + 4] = base + 2;
    indices[index + 5] = base + 3;
  }

  // One shared buffer for all four attributes: the whole field is static, so it
  // is uploaded once and never touched again.
  const buffer = new Buffer({
    data: vertices,
    label: 'atmosphere-dust',
    usage: BufferUsage.VERTEX | BufferUsage.COPY_DST,
  });

  return new Geometry({
    label: 'atmosphere-dust',
    attributes: {
      aPosition: { buffer, format: 'float32x2', stride: STRIDE, offset: 0 },
      aMote: { buffer, format: 'float32x4', stride: STRIDE, offset: 8 },
      aWander: { buffer, format: 'float32x4', stride: STRIDE, offset: 24 },
      aShape: { buffer, format: 'float32x4', stride: STRIDE, offset: 40 },
    },
    indexBuffer: indices,
  });
}

/* ------------------------------------------------------------------------ *
 * Reduced motion.
 * ------------------------------------------------------------------------ */

function prefersReducedMotion(): boolean {
  const media = (globalThis as { matchMedia?: (query: string) => MediaQueryList }).matchMedia;
  if (typeof media !== 'function') return false;
  try {
    return media.call(globalThis, '(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------------ */

export interface AtmosphereOptions {
  /**
   * Number of motes. Default 64. If the player can count them it is too many;
   * if they can name the effect it is far too many.
   */
  count?: number;
  /**
   * Global brightness, 1 = as designed. **0 hides the mesh outright**, so the
   * effect costs nothing at all when it is switched off. Default 1.
   */
  intensity?: number;
  /**
   * Freeze the drift. Omitted, it follows `prefers-reduced-motion` and keeps
   * following it if the user changes the setting mid-session. The dust stays —
   * still air is still air — it simply stops moving.
   */
  reducedMotion?: boolean;
  /**
   * Contrast of the visibility gradient; see {@link DUST.contrast}. Higher
   * confines the visible dust to a tighter pool under the key light.
   */
  contrast?: number;
  /** Peak additive alpha of the smallest mote. Default {@link DUST.alphaPeak}. */
  alpha?: number;
  /** Mote sigma range in design units. Default `[0.6, 1.9]`. */
  size?: readonly [number, number];
  /** Virtual height range above the felt. Default `[20, 150]`. */
  height?: readonly [number, number];
  /**
   * Drift speed, 1 = as designed. Implemented as a shorter loop rather than a
   * scaled velocity, which is what keeps every frequency an exact harmonic and
   * the field seamlessly periodic.
   */
  speed?: number;
  /** Field seed. The same seed always lays out the same dust. */
  seed?: number;
  /**
   * zIndex written on the view. Default 1, which puts it above the card drop
   * shadows when it is added to the `shadows` layer — dust floats in the air
   * over the felt, not underneath the marks on it.
   */
  zIndex?: number;
}

/**
 * Slow dust in the key light. Add {@link Atmosphere.view} to a layer *below*
 * the cards and drive {@link Atmosphere.update} from the per-frame loop.
 */
export class Atmosphere {
  /** One mesh, one draw call. Additive, and never interactive. */
  readonly view: Mesh<Geometry, Shader>;

  readonly #geometry: Geometry;
  readonly #shader: Shader;

  /**
   * The entire per-frame interface to the GPU: a clock and a dimmer. Declared
   * as a field initialiser so its type is inferred rather than restated.
   */
  readonly #uniforms = new UniformGroup({
    uTime: { value: 0, type: 'f32' },
    uIntensity: { value: 1, type: 'f32' },
  });

  readonly #loop: number;

  #time = 0;
  #intensity: number;
  #reduced: boolean;
  #motionQuery: MediaQueryList | null = null;
  #destroyed = false;

  constructor(options: AtmosphereOptions = {}) {
    const [sigmaMin, sigmaMax] = options.size ?? [DUST.sigmaMin, DUST.sigmaMax];
    const [heightMin, heightMax] = options.height ?? [DUST.heightMin, DUST.heightMax];

    this.#loop = DUST.loop / Math.max(options.speed ?? 1, 0.01);
    this.#intensity = Math.max(options.intensity ?? 1, 0);
    this.#reduced = options.reducedMotion ?? prefersReducedMotion();

    this.#geometry = buildGeometry({
      count: Math.max(1, Math.floor(options.count ?? DUST.count)),
      loop: this.#loop,
      sigmaMin: Math.max(sigmaMin, 0.05),
      sigmaMax: Math.max(sigmaMax, sigmaMin),
      alphaPeak: Math.max(options.alpha ?? DUST.alphaPeak, 0),
      heightMin,
      heightMax: Math.max(heightMax, heightMin),
      seed: options.seed ?? DUST.seed,
    });

    this.#uniforms.uniforms.uIntensity = this.#intensity;

    this.#shader = Shader.from({
      gl: {
        vertex: buildVertex({
          contrast: Math.max(options.contrast ?? DUST.contrast, 0.01),
          heightMin,
          heightMax: Math.max(heightMax, heightMin),
        }),
        fragment: FRAGMENT,
        name: 'atmosphere-dust',
      },
      resources: { dustUniforms: this.#uniforms },
    });

    this.view = new Mesh<Geometry, Shader>({
      geometry: this.#geometry,
      shader: this.#shader,
      label: 'atmosphere-dust',
    });
    this.view.blendMode = 'add';
    this.view.eventMode = 'none';
    this.view.zIndex = options.zIndex ?? 1;
    this.view.visible = this.#intensity > 0;
    // The geometry is a cloud of unit quads around the origin, so its own
    // bounds mean nothing. Anything asking this layer how big it is should be
    // told "the table".
    this.view.boundsArea = new Rectangle(0, 0, DESIGN.width, DESIGN.height);

    if (options.reducedMotion === undefined) this.#watchMotionPreference();
  }

  /* --------------------------------------------------------------- config -- */

  get intensity(): number {
    return this.#intensity;
  }

  /** 0 hides the mesh; the update path then costs nothing at all. */
  setIntensity(value: number): void {
    if (this.#destroyed) return;
    this.#intensity = Math.max(value, 0);
    this.#uniforms.uniforms.uIntensity = this.#intensity;
    this.view.visible = this.#intensity > 0;
  }

  get reducedMotion(): boolean {
    return this.#reduced;
  }

  /** Freezes the drift where it stands. The dust itself stays. */
  setReducedMotion(value: boolean): void {
    this.#reduced = value;
  }

  /* ------------------------------------------------------------------ sim -- */

  /**
   * Advance the field. `dt` is in seconds. One float write per frame — there is
   * no CPU-side simulation to run, and nothing here allocates.
   */
  update(dt: number): void {
    if (this.#destroyed || this.#reduced || this.#intensity <= 0) return;

    const step = dt > MAX_STEP ? MAX_STEP : dt;
    if (!(step > 0)) return;

    // Wrapped at the loop period, which the field is exactly periodic over, so
    // this is invisible and keeps the driving float small forever.
    this.#time = (this.#time + step) % this.#loop;
    this.#uniforms.uniforms.uTime = this.#time;
  }

  dispose(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;

    if (this.#motionQuery) {
      this.#motionQuery.removeEventListener('change', this.#onMotionPreferenceChange);
      this.#motionQuery = null;
    }

    this.view.removeFromParent();
    // Guarded: a host that tore its layer down with `{ children: true }` has
    // already taken the mesh, and destroying it twice is a warning, not a fix.
    if (!this.view.destroyed) this.view.destroy();
    this.#geometry.destroy(true);
    // `false`: GlProgram instances are cached globally by source, so the program
    // may outlive this instance and must not be pulled from under it.
    this.#shader.destroy(false);
  }

  /* ---------------------------------------------------------------- motion -- */

  #watchMotionPreference(): void {
    const media = (globalThis as { matchMedia?: (query: string) => MediaQueryList }).matchMedia;
    if (typeof media !== 'function') return;
    try {
      const query = media.call(globalThis, '(prefers-reduced-motion: reduce)');
      query.addEventListener('change', this.#onMotionPreferenceChange);
      this.#motionQuery = query;
    } catch {
      /* engines without matchMedia listeners simply keep the initial value */
    }
  }

  #onMotionPreferenceChange = (event: MediaQueryListEvent): void => {
    this.setReducedMotion(event.matches);
  };
}

/**
 * Build the atmosphere. Add `view` to a layer under the cards — `shadows` is
 * the right one — and call `update(dt)` once per frame.
 */
export function createAtmosphere(options: AtmosphereOptions = {}): Atmosphere {
  return new Atmosphere(options);
}
